/* ══════════════════════════════════════════════════════════════
   src/content/sources.ts
   Content sources: Core (SRD), Hope & Fear (final, "HF") and the
   Void playtest. Items mark their source with `source`; SRD items
   leave it out. Spelling varies by file ('Void' / 'void'), so all
   helpers compare case-insensitively.
   ══════════════════════════════════════════════════════════════ */

export type ContentVersion = 'hf' | 'void';
export type SourceLabel = 'Core' | 'HF' | 'Void';

export const SOURCE_OPTIONS = ['All', 'Core', 'Hope & Fear', 'Void'];

const FILTER_TO_LABEL: Record<string, SourceLabel> = { Core: 'Core', 'Hope & Fear': 'HF', Void: 'Void' };

export function sourceLabel(source?: string): SourceLabel {
  const s = source?.toLowerCase();
  if (s === 'hf') return 'HF';
  if (s === 'void') return 'Void';
  return 'Core';
}

export function matchesSource(filter: string, source?: string): boolean {
  return filter === 'All' || FILTER_TO_LABEL[filter] === sourceLabel(source);
}

/**
 * Keeps SRD items, and for anything published in both Hope & Fear and the
 * Void, only the version the reader chose. Items with a single version are
 * always kept (e.g. Void-only Blood Hunter in Hope & Fear mode).
 * `key` groups versions of the same thing: name by default, domain for cards.
 */
export function pickVersion<T extends { source?: string }>(
  items: T[],
  version: ContentVersion,
  key: (item: T) => string = (item) => (item as unknown as { name: string }).name,
): T[] {
  const chosen = new Set(items.filter((i) => i.source?.toLowerCase() === version).map(key));
  return items.filter((i) => {
    const s = i.source?.toLowerCase();
    return !s || s === version || !chosen.has(key(i));
  });
}
